/**
 * Level rules, duplicated from server/presets.py only to filter the form options:
 * the server is the authority and its 422 errors reach the Snackbar.
 */

import { DeviceConfigBase, DeviceConfigCreate } from './config';
import { Device } from './Device';

type SteeringMode = DeviceConfigBase['steering_mode'];

export const LEVELS = [0, 1, 2] as const;

export const ALLOWED_MODES: Readonly<Record<number, readonly SteeringMode[]>> = {
  0: ['multi_channel_gnss', 'external_reference'],
  1: ['multi_channel_gnss'],
  2: ['multi_channel_gnss', 'adsb', 'gnss_adsb'],
};
// Allowed at every level: PI, no CGGTTS, never ADS-B master
export const MAINTENANCE_MODES: readonly SteeringMode[] = ['single_channel_gnss', 'open_loop'];
export const ADSB_MODES: readonly SteeringMode[] = ['adsb', 'gnss_adsb'];

const FALLBACK_MODE: SteeringMode = 'multi_channel_gnss';
const KALMAN_MODES: readonly SteeringMode[] = ['multi_channel_gnss', ...ADSB_MODES];

// Controller imposed by the mode (server/presets.py forced_controller): the server overrides any other choice
export const forcedController = (mode: string): DeviceConfigBase['controller_type'] =>
  KALMAN_MODES.includes(mode as SteeringMode) ? 'kalman' : 'pi';

export const isModeAllowed = (level: number, mode: string): boolean => {
  const allowed = ALLOWED_MODES[level];
  if (!allowed) return false;
  return allowed.includes(mode as SteeringMode) || MAINTENANCE_MODES.includes(mode as SteeringMode);
};

// Devices an L2 may exclude as ADS-B master: L0 and L1, never itself
export const exclusionCandidates = (devices: Device[], selfId: number): Device[] =>
  devices.filter((d) => d.id !== selfId && (d.level === 0 || d.level === 1));

// New form with the mode, the controller and the exclusions coherent with the level
export const normalizeForLevel = (form: DeviceConfigCreate): DeviceConfigCreate => {
  const steering_mode = isModeAllowed(form.level, form.steering_mode) ? form.steering_mode : FALLBACK_MODE;
  return {
    ...form,
    steering_mode,
    controller_type: forcedController(steering_mode),
    adsb_excluded_master_ids: form.level === 2 ? [...(form.adsb_excluded_master_ids ?? [])] : [],
  };
};
